import React, { FunctionComponent, useEffect } from "react";
import { useParams } from "react-router";
import { loadStripe } from "@stripe/stripe-js";
import { Elements as StripeElements, useElements, useStripe } from "@stripe/react-stripe-js";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useFeedSignupPageDataQuery, useSignupToFeedMutation } from "@helloaudio/api";
import { CustomButton } from "@helloaudio/ui";
import FeedDetails from "./FeedDetails";
import SignupForm, { PaymentType, useSignupForm } from "./SignupForm";
// import TrackingCode from "./TrackingCode";
import Tabs from "./Tabs";
import clsx from "clsx";
import PriceBreakdown from "./PriceBreakdown";
import SuccessModal from "./SuccessModal";
import AlreadyExistsModal from "./AlreadyExistsModal";
import PaymentErrorModal from "./PaymentErrorModal";
import { HeadInjection } from "@helloaudio/containers";

enum Mode {
  Off,
  Test,
  Live,
}

const FeedSignupLayout: FunctionComponent = () => {
  const { feedId } = useParams<{ feedId: string }>();
  /**
   * Destroy helpscout beacon on page load.
   */
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
    };
  }, []);

  const stripe = useStripe();
  const elements = useElements();

  const { data } = useFeedSignupPageDataQuery(feedId!);
  const {
    mutate: signupToFeed,
    isLoading: isMutationInFlight,
    data: mutationResult,
    reset: resetMutationResult,
  } = useSignupToFeedMutation();

  const form = useSignupForm(feedId!);

  const handleSignup = form.handleSubmit(async (values) => {
    if (form.pricingOption?.paymentType === PaymentType.Free) {
      signupToFeed({
        pricingOptionId: values.pricingOptionId,
        payload: {
          name: values.name,
          phone: values.phone,
          email: values.email,
        },
      });
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement("card")!;
    const result = await stripe.createToken(cardElement);
    if (result.error) {
      // TODO: handle error
    }
    if (result.token) {
      signupToFeed({
        pricingOptionId: values.pricingOptionId,
        payload: {
          name: values.name,
          phone: values.phone,
          email: values.email,
          token: result.token,
        },
      });
    }
  });

  const customColor =
    data!.shouldUseCustomColor && data!.customColor ? data!.customColor : undefined;
  const isTrial = data?.isTrial;

  if (data!.mode === Mode.Off) {
    return <>Page not found</>; // TODO: Refactor to return actual not found page
  }

  if (data!.mode === Mode.Live) {
    return (
      <React.Fragment>
        {data?.signupPageHeader && <HeadInjection rawHtml={data.signupPageHeader} />}
        <div>
          {/* TODO: Fix later */}
          {/* <TrackingCode feedId={feedId!} /> */}
          <SignupForm {...form}>
            <div className="tw-min-h-screen tw-grid md:tw-grid-cols-2">
              {/* Left side */}
              <div className="tw-bg-transparent">
                <FeedDetails feedId={feedId!} />
                <section className="tw-px-4 tw-my-4 md:tw-px-32 md:tw-my-16">
                  <SignupForm.PricingOptionsOptionGroup />
                </section>
              </div>

              {/* Right side */}
              <div className="tw-bg-white">
                <Tabs
                  className="tw-mb-4 md:tw-my-20 md:tw-fixed tw-px-4 md:tw-px-16 md:tw-w-1/2"
                  customColor={
                    data!.shouldUseCustomColor && data!.customColor ? data!.customColor : undefined
                  }
                  tabs={[
                    { label: "Contact Information" },
                    {
                      label: "Payment Information",
                      hidden: form.pricingOption?.paymentType === PaymentType.Free,
                    },
                  ]}
                  selectedIndex={form.step}
                  onChange={(index) => {
                    if (form.step === 1 && index === 0) form.previous();
                    if (form.step === 0 && index === 1) form.next();
                  }}
                >
                  {/* First step */}
                  {form.step === 0 && (
                    <form
                      className="tw-mt-4 md:tw-mt-10"
                      onSubmit={
                        form.pricingOption?.paymentType !== PaymentType.Free
                          ? form.next
                          : handleSignup
                      }
                    >
                      <SignupForm.NameField className="tw-mb-5" />
                      <SignupForm.EmailField className="tw-mb-5" />
                      <SignupForm.PhoneField className="tw-mb-5" />

                      {form.pricingOption?.paymentType !== PaymentType.Free && (
                        <CustomButton
                          style={customColor ? { backgroundColor: customColor } : undefined}
                          type="submit"
                          className="tw-w-full tw-justify-center tw-mt-[3.75rem]"
                          accent="primary"
                          trailingIcon={ChevronRightIcon}
                        >
                          Next step
                        </CustomButton>
                      )}

                      {form.pricingOption?.paymentType === PaymentType.Free && (
                        <>
                          <SignupForm.AgreementCheckbox className="tw-mb-5 tw-mt-10 tw-max-h-72 tw-overflow-y-auto" />
                          <CustomButton
                            style={customColor ? { backgroundColor: customColor } : undefined}
                            type="submit"
                            className="tw-w-full tw-justify-center"
                            accent="primary"
                            disabled={isMutationInFlight || isTrial}
                            loading={isMutationInFlight}
                          >
                            Sign Up
                          </CustomButton>
                          {isTrial && (
                            <p className="tw-text-sm tw-text-gray-500 tw-mt-2">
                              This feed belongs to a trial plan. While on a trial plan, listeners
                              cannot sign up for this feed.
                            </p>
                          )}
                        </>
                      )}
                    </form>
                  )}

                  {/* Second step */}
                  {/* WARNING: Stripe CardElement must always be present. This is why we hide with css. */}
                  <div className={clsx("tw-mt-10", form.step !== 1 && "tw-hidden")}>
                    <PriceBreakdown className="tw-mb-10" pricingOption={form.pricingOption} />
                    <form>
                      <SignupForm.CardField className="tw-my-10" />
                      {form.pricingOption?.paymentType !== PaymentType.Free && (
                        <SignupForm.AgreementCheckbox />
                      )}
                      <CustomButton
                        style={customColor ? { backgroundColor: customColor } : undefined}
                        className="tw-mt-4 tw-mb-3 tw-w-full tw-justify-center"
                        accent="primary"
                        disabled={isMutationInFlight || isTrial}
                        loading={isMutationInFlight}
                        onClick={handleSignup}
                      >
                        Sign Up
                      </CustomButton>
                      {isTrial && (
                        <p className="tw-text-sm tw-text-gray-500 tw-mt-2">
                          This feed belongs to a trial plan. While on a trial plan, listeners cannot
                          sign up for this feed.
                        </p>
                      )}
                    </form>
                  </div>
                </Tabs>
              </div>
            </div>
          </SignupForm>

          {/* Modals */}
          <SuccessModal
            visible={mutationResult?.status === 200}
            customColor={customColor}
            onContinue={() => {
              const subscribePageLink = mutationResult?.data.subscribePageLink;
              if (subscribePageLink) window.open(subscribePageLink, "_blank");
            }}
          />
          <AlreadyExistsModal
            visible={mutationResult?.status === 409}
            onClose={resetMutationResult}
          />

          <PaymentErrorModal
            visible={![200, 409, undefined].includes(mutationResult?.status)}
            onClose={resetMutationResult}
          />
        </div>
      </React.Fragment>
    );
  }

  return null;
};

const stripePromise = loadStripe(process.env.STRIPE_PK);
const FeedSignup: FunctionComponent = () => {
  return (
    <StripeElements stripe={stripePromise}>
      <FeedSignupLayout />
    </StripeElements>
  );
};

export default FeedSignup;
